import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Button, DropdownMenu, Icon, Select, Table, Text, TextInput } from '@gravity-ui/uikit';
import { Ellipsis, Magnifier, Pencil, Plus, Tag, TrashBin } from '@gravity-ui/icons';
import type { PlatformExpenseView } from '@odonix/contracts';
import { useFinanceApi } from '../../lib/api/finance-context.js';
import { count, day, rub } from '../../ui/format.js';
import { MetricStrip } from '../../ui/metric-strip.js';
import { Page, Section } from '../../ui/page.js';
import { PeriodPicker } from '../../ui/period-picker.js';
import { periodOf } from '../../ui/period.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';
import { CategoriesDialog, DeleteExpenseDialog, ExpenseDialog, useCategories } from './expense-dialogs.js';

/**
 * Расходы платформы: категория, на что, сумма, комментарий. Попадают в «Ушло»
 * на обзоре рядом с процентами и премиями менеджеров.
 *
 * Период и отбор — в адресе: обзор ведёт сюда «расходами октября по рекламе»
 * готовой ссылкой.
 */

const routeApi = getRouteApi('/shell/expenses');
const ANY = '__any';

type Editing = { kind: 'create' } | { kind: 'edit'; expense: PlatformExpenseView } | { kind: 'delete'; expense: PlatformExpenseView } | { kind: 'categories' };

export function ExpensesScreen() {
  const api = useFinanceApi();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const period = periodOf(search);
  const categories = useCategories();
  const [qDraft, setQDraft] = useState(search.q ?? '');
  const [editing, setEditing] = useState<Editing | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['expenses', period.from, period.to, search.categoryId, search.q],
    queryFn: ({ pageParam }) =>
      api.expenses({ period, categoryId: search.categoryId, q: search.q, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: (previous) => previous,
  });

  const expenses = query.data?.pages.flatMap((page) => page.expenses) ?? [];
  const totals = query.data?.pages[0]?.totals;
  const top = totals?.byCategory[0];
  const close = () => {
    setEditing(null);
  };

  function submitSearch(value: string): void {
    const q = value.trim();
    if (q === (search.q ?? '')) return;
    void navigate({ search: (prev) => ({ ...prev, q: q === '' ? undefined : q }) });
  }

  return (
    <Page
      title="Расходы"
      description="Траты платформы по категориям. Входят в «Ушло» на обзоре вместе с процентами и премиями менеджеров."
      actions={
        <>
          <PeriodPicker
            value={period}
            onChange={(next) => {
              void navigate({ search: (prev) => ({ ...prev, from: next.from, to: next.to }) });
            }}
          />
          <Button
            view="outlined"
            onClick={() => {
              setEditing({ kind: 'categories' });
            }}
          >
            <Icon data={Tag} size={16} />
            Категории
          </Button>
          <Button
            view="action"
            onClick={() => {
              setEditing({ kind: 'create' });
            }}
          >
            <Icon data={Plus} size={16} />
            Добавить расход
          </Button>
        </>
      }
    >
      <MetricStrip
        loading={totals === undefined}
        metrics={[
          { label: 'Потрачено за период', value: rub(totals?.amountMinor ?? 0) },
          { label: 'Записей', value: count(totals?.count ?? 0) },
          {
            label: 'Больше всего',
            value: top === undefined ? 'нет данных' : top.name,
            hint: top === undefined ? undefined : rub(top.amountMinor),
          },
        ]}
      />

      <div className="flex flex-wrap items-end gap-3">
        <form
          className="w-full sm:w-72"
          onSubmit={(event) => {
            event.preventDefault();
            submitSearch(qDraft);
          }}
        >
          <TextInput
            size="m"
            placeholder="На что или комментарий"
            value={qDraft}
            onUpdate={setQDraft}
            onBlur={() => {
              submitSearch(qDraft);
            }}
            hasClear
            startContent={<Icon data={Magnifier} size={16} className="ml-2 text-[var(--g-color-text-secondary)]" />}
            controlProps={{ 'aria-label': 'Поиск расхода' }}
          />
        </form>
        <Select
          size="m"
          width={240}
          label="Категория:"
          value={[search.categoryId ?? ANY]}
          options={[
            { value: ANY, content: 'Все' },
            ...(categories.data ?? []).map((category) => ({
              value: category.id,
              content: category.archivedAt === null ? category.name : `${category.name} (архив)`,
            })),
          ]}
          onUpdate={(values) => {
            const value = values[0];
            void navigate({ search: (prev) => ({ ...prev, categoryId: value === ANY ? undefined : value }) });
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <Section flush>
          {query.isError ? (
            <div className="px-5 py-3">
              <ErrorNote title="Расходы не загрузились" error={query.error} onRetry={() => void query.refetch()} />
            </div>
          ) : query.isPending ? (
            <div className="px-5 py-3">
              <LoadingRows rows={6} />
            </div>
          ) : expenses.length === 0 ? (
            <div className="px-5">
              <Empty
                title={search.categoryId !== undefined || search.q !== undefined ? 'По этому отбору расходов нет' : 'За период расходов нет'}
                description="Добавьте первый: категория, на что, сумма и дата — комментарий по желанию."
                action={
                  <Button
                    view="action"
                    onClick={() => {
                      setEditing({ kind: 'create' });
                    }}
                  >
                    Добавить расход
                  </Button>
                }
              />
            </div>
          ) : (
            <div>
              <Table
                className="w-full"
                edgePadding
                stickyHorizontalScroll
                data={expenses}
                getRowDescriptor={(row) => ({ id: row.id })}
                columns={[
                  { id: 'date', name: 'Дата', template: (row) => <span className="tabular">{day(row.spentOn)}</span>, width: 130 },
                  { id: 'category', name: 'Категория', template: (row) => row.categoryName },
                  {
                    id: 'title',
                    name: 'Расход',
                    template: (row) => (
                      <span className="flex min-w-48 flex-col">
                        <span>{row.title}</span>
                        {row.comment === null ? null : (
                          <Text variant="caption-2" color="secondary">
                            {row.comment}
                          </Text>
                        )}
                      </span>
                    ),
                  },
                  {
                    id: 'amount',
                    name: 'Сумма',
                    align: 'end',
                    template: (row) => <span className="tabular whitespace-nowrap">{rub(row.amountMinor)}</span>,
                  },
                  {
                    id: 'by',
                    name: 'Добавил',
                    template: (row) => <Text color="secondary">{row.createdByEmail ?? '—'}</Text>,
                  },
                  {
                    id: 'actions',
                    name: '',
                    align: 'end',
                    width: 56,
                    template: (row) => (
                      <DropdownMenu
                        renderSwitcher={(props) => (
                          <Button {...props} view="flat" size="s" aria-label={`Действия с расходом «${row.title}»`}>
                            <Icon data={Ellipsis} size={16} />
                          </Button>
                        )}
                        items={[
                          {
                            text: 'Изменить',
                            iconStart: <Icon data={Pencil} size={16} />,
                            action: () => {
                              setEditing({ kind: 'edit', expense: row });
                            },
                          },
                          {
                            text: 'Удалить',
                            theme: 'danger',
                            iconStart: <Icon data={TrashBin} size={16} />,
                            action: () => {
                              setEditing({ kind: 'delete', expense: row });
                            },
                          },
                        ]}
                      />
                    ),
                  },
                ]}
              />
              {query.hasNextPage ? (
                <div className="flex justify-center px-5 py-3">
                  <Button view="outlined" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                    Показать ещё
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </Section>

        <Section title="По категориям">
          {totals === undefined ? (
            <LoadingRows rows={4} height={24} />
          ) : totals.byCategory.length === 0 ? (
            <Text color="secondary">Нет расходов за период.</Text>
          ) : (
            <ul className="flex flex-col gap-3">
              {totals.byCategory.map((row) => (
                <li key={row.categoryId} className="flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <button
                      type="button"
                      className="cursor-pointer border-0 bg-transparent p-0 text-left text-[var(--g-color-text-link)] hover:text-[var(--g-color-text-link-hover)]"
                      onClick={() => {
                        void navigate({ search: (prev) => ({ ...prev, categoryId: row.categoryId }) });
                      }}
                    >
                      <Text>{row.name}</Text>
                    </button>
                    <Text className="tabular" whiteSpace="nowrap">
                      {rub(row.amountMinor)}
                    </Text>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-[var(--g-color-base-generic)]">
                    <div
                      className="h-full rounded-full bg-[var(--g-color-base-brand)]"
                      style={{ width: `${String(totals.amountMinor === 0 ? 0 : (row.amountMinor / totals.amountMinor) * 100)}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {editing?.kind === 'create' ? <ExpenseDialog expense={null} onClose={close} /> : null}
      {editing?.kind === 'edit' ? <ExpenseDialog expense={editing.expense} onClose={close} /> : null}
      {editing?.kind === 'delete' ? <DeleteExpenseDialog expense={editing.expense} onClose={close} /> : null}
      {editing?.kind === 'categories' ? <CategoriesDialog onClose={close} /> : null}
    </Page>
  );
}
